import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useInvestigations } from "@/lib/intel/store";
import {
  Search, FolderOpen, FileDigit, FileText, Network, Clock, ShieldAlert,
  Radio, Map as MapIcon, Database, Users, Bot, Settings, LayoutDashboard, Activity,
  Archive, Image as ImageIcon, Radar, BookOpen,
} from "lucide-react";

let openState = false;
let pendingQuery = "";
const subs = new Set<() => void>();
function setOpenState(v: boolean, query = "") { openState = v; pendingQuery = query; subs.forEach((f) => f()); }
function subscribe(f: () => void) { subs.add(f); return () => { subs.delete(f); }; }
function getSnap() { return openState; }
function getServerSnap() { return false; }
function useOpen() { return useSyncExternalStore(subscribe, getSnap, getServerSnap); }

export function useCommandPalette() {
  return (query = "") => setOpenState(true, query);
}

type Item = {
  id: string; label: string; sub?: string;
  group: "Cases" | "Evidence" | "Notes" | "Navigate";
  icon: typeof Search;
  go: () => void;
};

export function CommandPalette() {
  const open = useOpen();
  const setOpen = setOpenState;
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const { state } = useInvestigations();

  // Hotkeys
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      if ((isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault(); setOpen(!openState, "");
      } else if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => { if (open) { setQ(pendingQuery); setActive(0); } }, [open]);

  const items = useMemo<Item[]>(() => {
    const nav: Item[] = [
      { id: "n-dash", group: "Navigate", icon: LayoutDashboard, label: "Investigate (command search)", go: () => navigate({ to: "/dashboard" }) },
      { id: "n-inv",  group: "Navigate", icon: FolderOpen, label: "Case Workspace", go: () => navigate({ to: "/investigate" }) },
      { id: "n-graph",group: "Navigate", icon: Network, label: "Relationship graph", go: () => navigate({ to: "/graph" }) },
      { id: "n-time", group: "Navigate", icon: Clock, label: "Timeline", go: () => navigate({ to: "/timeline" }) },
      { id: "n-thr",  group: "Navigate", icon: ShieldAlert, label: "Threat center", go: () => navigate({ to: "/threats" }) },
      { id: "n-sim",  group: "Navigate", icon: Radar, label: "Threat simulation", go: () => navigate({ to: "/threat-simulation" }) },
      { id: "n-mon",  group: "Navigate", icon: Radio, label: "Live monitoring", go: () => navigate({ to: "/monitor" }) },
      { id: "n-geo",  group: "Navigate", icon: MapIcon, label: "GEOINT", go: () => navigate({ to: "/geo" }) },
      { id: "n-img",  group: "Navigate", icon: ImageIcon, label: "Image intelligence", go: () => navigate({ to: "/imagery" }) },
      { id: "n-src",  group: "Navigate", icon: Database, label: "Source registry", go: () => navigate({ to: "/sources" }) },
      { id: "n-cas",  group: "Navigate", icon: FolderOpen, label: "Case files", go: () => navigate({ to: "/cases" }) },
      { id: "n-vault",group: "Navigate", icon: Archive, label: "Scan vault", go: () => navigate({ to: "/vault" }) },
      { id: "n-rep",  group: "Navigate", icon: FileText, label: "Reports", go: () => navigate({ to: "/reports" }) },
      { id: "n-team", group: "Navigate", icon: Users, label: "Analysts", go: () => navigate({ to: "/team" }) },
      { id: "n-asst", group: "Navigate", icon: Bot, label: "AI Analyst", go: () => navigate({ to: "/assistant" }) },
      { id: "n-guide",group: "Navigate", icon: BookOpen, label: "User guide", go: () => navigate({ to: "/guide" }) },
      { id: "n-set",  group: "Navigate", icon: Settings, label: "Settings", go: () => navigate({ to: "/settings" }) },
    ];
    const cases: Item[] = state.cases.map((c) => ({
      id: c.id, group: "Cases", icon: FolderOpen,
      label: `${c.id} · ${c.title}`, sub: `→ ${c.target}`,
      go: () => navigate({ to: "/investigate" }),
    }));
    const evidence: Item[] = state.evidence.map((e) => ({
      id: e.id, group: "Evidence", icon: FileDigit,
      label: e.label, sub: `${e.kind.toUpperCase()} · ${e.source} · ${e.tags.map((t) => "#" + t).join(" ")}`,
      go: () => navigate({ to: "/investigate" }),
    }));
    const notes: Item[] = state.notes.map((n) => ({
      id: n.id, group: "Notes", icon: FileText,
      label: n.body.slice(0, 80), sub: `${n.id} · ${n.author}`,
      go: () => navigate({ to: "/investigate" }),
    }));
    return [...nav, ...cases, ...evidence, ...notes];
  }, [state, navigate]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items.slice(0, 30);
    const needle = q.toLowerCase();
    return items
      .map((i) => ({ i, score: rank(i, needle) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 50)
      .map((x) => x.i);
  }, [items, q]);

  if (!open) return null;

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const x = filtered[active]; if (x) { x.go(); setOpen(false); } }
  };

  // Group order
  const groups: Item["group"][] = ["Navigate", "Cases", "Evidence", "Notes"];

  return (
    <div className="fixed inset-0 z-[60] grid place-items-start justify-center bg-background/80 backdrop-blur-sm pt-24 px-4" onClick={() => setOpen(false)}>
      <div onClick={(e) => e.stopPropagation()} className="glass-strong w-full max-w-2xl rounded-2xl ring-glow overflow-hidden">
        <div className="flex items-center gap-2 border-b border-panel-border px-4 py-3">
          <Search className="h-4 w-4 text-cyan" />
          <input
            autoFocus value={q} onChange={(e) => { setQ(e.target.value); setActive(0); }}
            onKeyDown={onKey}
            placeholder="Search cases, evidence, notes, entities, findings, navigate…"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="rounded border border-panel-border bg-background/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-2">
          {filtered.length === 0 && (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground">No matches.</div>
          )}
          {groups.map((g) => {
            const list = filtered.filter((f) => f.group === g);
            if (!list.length) return null;
            return (
              <div key={g} className="mb-2">
                <div className="px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{g}</div>
                {list.map((i) => {
                  const idx = filtered.indexOf(i);
                  const isActive = idx === active;
                  const Icon = i.icon;
                  return (
                    <button key={i.id}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => { i.go(); setOpen(false); }}
                      className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors ${isActive ? "bg-cyan/10 ring-1 ring-cyan/30" : "hover:bg-white/5"}`}>
                      <Icon className={`h-4 w-4 ${isActive ? "text-cyan" : "text-muted-foreground"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm">{i.label}</div>
                        {i.sub && <div className="truncate font-mono text-[10px] text-muted-foreground">{i.sub}</div>}
                      </div>
                      <span className="font-mono text-[10px] text-muted-foreground">{i.group}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="border-t border-panel-border px-4 py-2 flex items-center gap-3 font-mono text-[10px] text-muted-foreground">
          <span><kbd className="rounded bg-background/60 px-1">↑↓</kbd> navigate</span>
          <span><kbd className="rounded bg-background/60 px-1">↵</kbd> open</span>
          <span className="ml-auto">{filtered.length} results · ⌘K to toggle</span>
        </div>
      </div>
    </div>
  );
}

function rank(item: Item, needle: string): number {
  const hay = `${item.label} ${item.sub ?? ""} ${item.group}`.toLowerCase();
  if (hay.includes(needle)) {
    let s = 10;
    if (item.label.toLowerCase().startsWith(needle)) s += 20;
    if (item.label.toLowerCase().includes(needle)) s += 10;
    return s;
  }
  // fuzzy: every char in order
  let i = 0;
  for (const c of needle) {
    const j = hay.indexOf(c, i);
    if (j === -1) return 0;
    i = j + 1;
  }
  return 1;
}